// CUSTOM: personalised-review-data.js
// Review page data loading module.
// Handles all data-loading concerns for the personalised review page:
//   waitForProductDataReady, initSubscriptionData, loadFromRechargeSubscription, loadBundleData.
// Depends on: personalised-config.js, personalised-utils.js, personalised-recharge-utils.js,
//   personalised-bundle-state.js, personalised-bundle-ui.js, personalised-validation.js,
//   personalised-storage-manager.js, personalised-recharge-session.js (eventBus).

(function() {
  window.personalised = window.personalised || {};

  const reviewData = (function() {

    // State loading lock — prevents concurrent loadBundleData calls
    let isLoading = false;

    // =============================================================================
    // ===== SHARED HELPERS =====
    // =============================================================================

    /**
     * Wait for product data to be available before proceeding with rendering.
     * Uses polling with a timeout to avoid blocking the UI indefinitely.
     * @returns {Promise<void>}
     */
    async function waitForProductDataReady() {
      const maxWait = 3000; // 3 second timeout
      const pollInterval = 50; // Check every 50ms
      let waited = 0;

      // First check if data is already available
      if (typeof window.personalised.productData === 'object' &&
          Object.keys(window.personalised.productData).length > 0) {
        if (window.personalised?.debugMode) console.log('Product data already available');
        return;
      }

      if (window.personalised?.debugMode) console.log('Waiting for product data to load...');

      // If not available, poll until timeout
      while (waited < maxWait) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        waited += pollInterval;

        if (typeof window.personalised.productData === 'object' &&
            Object.keys(window.personalised.productData).length > 0) {
          if (window.personalised?.debugMode) console.log(`Product data loaded after ${waited}ms`);
          return;
        }
      }

      // Log warning if product data still not available after timeout
      if (window.personalised?.debugMode) console.warn('Timeout waiting for productData to load after 3 seconds');
    }

    // =============================================================================
    // ===== EDIT MODE ONLY =====
    // =============================================================================

    /**
     * [EDIT MODE ONLY] Initializes subscription data when in edit mode.
     * Retrieves and stores subscription details from Recharge for use throughout the bundle experience.
     * @returns {Promise<void>}
     */
    async function initSubscriptionData() {
      const config = window.personalised.config;
      window.personalised?.debug?.time('review:initSubscriptionData');
      window.personalised?.debug?.trace('review:initSubscriptionDataStart', {});
      try {
        // Initialize mode using the new unified approach
        await window.personalised.utils.initMode();
        const mode = window.personalised.utils.getMode();
        const subscriptionId = window.personalised.utils.getCurrentSubscriptionId();

        // Only proceed with subscription data if in edit mode
        if (mode === config.modes.EDIT && subscriptionId) {
          // If we have a subscription ID, store it for later
          // Use extractEssentialSubscriptionData to standardize data format
          const essentialData = window.personalised.utils.extractEssentialSubscriptionData({ id: subscriptionId });
          // Store in bundle metadata
          window.personalised.bundle.setMetadata('subscriptionDetails', essentialData);
          if (window.personalised?.debugMode) console.log('Using subscription ID from session:', subscriptionId);

          // Only attempt Recharge operations in edit mode
          if (mode === config.modes.EDIT && typeof window.personalised.rechargeUtils?.getSubscription === 'function') {
            try {
              const pack = await window.personalised.rechargeUtils.getSubscription(subscriptionId);
              if (pack) {
                // Extract essential data and store in bundle metadata
                const essentialData = window.personalised.utils.extractEssentialSubscriptionData(pack);
                window.personalised.bundle.setMetadata('subscriptionDetails', essentialData);
                // PR-5.3: Store normalised subscription state fields for later UX consumption.
                // These override the values set during findPersonalisedPackSubscription() with
                // fresher data from the full subscription fetch.
                const _storeMgr = window.personalised.storageManager;
                if (_storeMgr) {
                  _storeMgr.setMetadata('subscriptionStatus', pack.status || null);
                  _storeMgr.setMetadata('subscriptionCreatedAt', pack.created_at || null);
                  _storeMgr.setMetadata('subscriptionUpdatedAt', pack.updated_at || null);
                  _storeMgr.setMetadata('nextChargeScheduledAt', pack.next_charge_scheduled_at || null);
                  _storeMgr.setMetadata('orderIntervalFrequency', pack.order_interval_frequency != null ? String(pack.order_interval_frequency) : null);
                }
              }
            } catch (e) {
              if (window.personalised?.debugMode) console.warn('Failed to fetch full subscription details:', e);
            }
          }
        }

        if (window.personalised?.debugMode) console.log('Bundle mode:', mode);
        window.personalised?.debug?.timeEnd('review:initSubscriptionData');
      } catch (err) {
        window.personalised?.debug?.timeEnd('review:initSubscriptionData');
        if (window.personalised?.debugMode) console.error('Error initializing customer session:', err);
        // Continue in current mode even if initialization fails
      }
    }

    /**
     * [EDIT MODE ONLY] Performs a single Recharge bundle fetch and loads the result into bundle state.
     * Handles Recharge API call, loadFromRecharge, variant lookup build, and auth-error recovery.
     * @param {string|number} subscriptionId - The Recharge subscription ID to fetch for.
     * @returns {Promise<boolean>} true if bundle was successfully loaded from Recharge, false otherwise.
     */
    async function loadFromRechargeSubscription(subscriptionId) {
      try {
        const bundleData = await window.personalised.rechargeUtils.getBundleSelectionForSubscription(subscriptionId);
        if (bundleData) {
          window.personalised.bundle.loadFromRecharge(bundleData);
          if (window.personalised.bundleUI?.buildVariantLookup) {
            if (window.personalised?.debugMode) console.log('Building variant lookup with Recharge data');
            window.personalised.bundleUI.buildVariantLookup();
          }
          if (window.personalised?.debugMode) console.log('Successfully loaded bundle from Recharge subscription');
          return true;
        }
      } catch (rechargeErr) {
        if (window.personalised.utils?.handleAuthError) {
          if (window.personalised.utils.handleAuthError(rechargeErr, 'loadFromRechargeSubscription')) {
            window.personalised.utils.setCurrentMode('guest', { source: 'auth-error-recovery' });
          }
        }
        if (window.personalised?.debugMode) console.error('Failed to load bundle from Recharge:', rechargeErr);
      }
      return false;
    }

    // =============================================================================
    // ===== SHARED — DATA LOADING =====
    // =============================================================================

    /**
     * [SHARED] Loads bundle data based on current mode.
     * Routes to Recharge (edit) or localStorage (guest/new) based on mode and session state.
     * @returns {Promise<boolean>} True if bundle was loaded from Recharge, false if from localStorage.
     */
    async function loadBundleData() {
      const config = window.personalised.config;

      if (isLoading) {
        window.personalised?.debug?.trace('review:isLoadingGuardBlocked', {});
        return false;
      }
      isLoading = true;

      try {
        // CRITICAL: Ensure mode is initialized before proceeding
        await window.personalised.utils.initMode();
        if (window.personalised.debugMode) console.debug('[Bundle Load] Mode initialization confirmed before bundle loading');

        // Now safely check session freshness AFTER mode is initialized
        // PR-4.3: route through storageManager (single authority for SESSION_FRESH key)
        const isSessionFresh = window.personalised.storageManager.isSessionFresh();
        const mode = window.personalised.utils.getMode();
        if (window.personalised?.debugMode) console.log(`Initializing bundle state in ${mode} mode${isSessionFresh ? ' (fresh session)' : ''}`);
        if (window.personalised?.debugMode) console.debug('[Bundle Load] Session freshness check:', isSessionFresh ? 'FRESH - will prioritize Recharge data' : 'NORMAL - using standard load logic');

        // — EDIT PATH (fresh session): bypass local state and load from Recharge immediately —
        if (isSessionFresh &&
            mode === config.modes.EDIT &&
            window.personalised.utils.getSubscriptionDetails()?.id) {
          if (window.personalised?.debugMode) console.debug('[Bundle Load] Fresh session detected - bypassing local state and loading from Recharge');

          const loaded = await loadFromRechargeSubscription(window.personalised.utils.getSubscriptionDetails()?.id);
          if (loaded) {
            window.personalised?.debug?.trace('review:bundleDataSource', { source: 'recharge', path: 'fresh-session' });
            // Clear the session freshness flag after successful load
            window.personalised.storageManager.clearSessionFresh(); // PR-4.3
            window.personalised.storageManager.clearModifiedFlag(); // PR-6.1: bundle now reflects live Recharge data
            if (window.personalised?.debugMode) console.log('Cleared session freshness flag after Recharge load');
            return true;
          }
          if (window.personalised?.debugMode) console.debug('[Bundle Load] Falling back to standard load process after Recharge failure');
          // Continue with standard load logic if Recharge load fails
        }

        // Check for local modifications flag using storageManager
        const stateModified = window.personalised.storageManager.isBundleModified();

        // Get bundle state with validation using storageManager
        let hasValidLocalState = false;
        try {
          const saved = window.personalised.storageManager.getBundleState();
          window.personalised.bundle.load();
          hasValidLocalState = window.personalised.validation.validateBundleLoaded();
        } catch (e) {
          if (window.personalised?.debugMode) console.warn('Error validating local state:', e);
        }

        if (window.personalised?.debugMode) console.log(`[Bundle Debug] State check: modified=${stateModified}, hasValidLocalState=${hasValidLocalState}`);

        // Standard logic to determine if we should use Recharge or local state
        const shouldUseRecharge = mode === config.modes.EDIT &&
                               window.personalised.utils.getSubscriptionDetails()?.id &&
                               (!isSessionFresh && (!stateModified || !hasValidLocalState));

        // If we reach here and session is still fresh, clear the flag
        if (isSessionFresh) {
          window.personalised.storageManager.clearSessionFresh(); // PR-4.3
          if (window.personalised?.debugMode) console.log('Fresh session flag still present - cleared session freshness flag');
          if (window.personalised?.debugMode) console.debug('[Bundle Load] Continuing with standard load logic after clearing freshness flag');
        }

        // — EDIT PATH (standard): use Recharge when not modified locally —
        if (shouldUseRecharge) {
          if (window.personalised?.debugMode) console.log('Edit mode detected - loading bundle data from Recharge (standard path)');
          const loaded = await loadFromRechargeSubscription(window.personalised.utils.getSubscriptionDetails()?.id);
          if (loaded) {
            window.personalised?.debug?.trace('review:bundleDataSource', { source: 'recharge', path: 'standard' });
            window.personalised.storageManager.clearModifiedFlag(); // PR-6.1: bundle now reflects live Recharge data
            return true;
          }
          // Fallback to localStorage only if Recharge load fails
          if (window.personalised?.debugMode) console.log('Falling back to localStorage due to Recharge load failure');
          window.personalised?.debug?.trace('review:bundleDataSource', { source: 'localStorage', path: 'recharge-fallback', mode: mode });
          window.personalised.bundle.load();
        } else {
          // — GUEST/NEW PATH (and edit with local modifications): load from localStorage —
          if (mode === config.modes.EDIT) {
            if (stateModified) {
              if (window.personalised?.debugMode) console.log('Edit mode with local modifications - using localStorage instead of Recharge data');
            } else {
              if (window.personalised?.debugMode) console.log('Edit mode with no valid local state - using localStorage');
            }
          }
          const loaded = window.personalised.bundle.load();
          window.personalised?.debug?.trace('review:bundleDataSource', { source: 'localStorage', path: 'standard', mode: mode });
          if (window.personalised?.debugMode) console.log(`Loaded bundle state from localStorage in ${mode} mode`);

          // Ensure UI consistency after loading from localStorage
          if (loaded) {
            // Use the new ensureVariantLookupBuilt helper if available, fall back to buildVariantLookup
            if (window.personalised.bundleUI?.ensureVariantLookupBuilt) {
              window.personalised.bundleUI.ensureVariantLookupBuilt();
            } else if (window.personalised.bundleUI?.buildVariantLookup) {
              window.personalised.bundleUI.buildVariantLookup();
            }
            window.personalised.eventBus.emit('bundleStateLoaded', {
              source: 'localStorage',
              hasItems: window.personalised.validation.bundleHasItems(),
              itemCount: window.personalised.bundle.getItemCount(), // Keep for backward compatibility
              frequency: window.personalised.bundle.getFrequency(),
              isValid: window.personalised.validation.validateBundleLoaded()
            });
            if (window.personalised?.debugMode) console.log('Local state loaded and UI sync triggered');
          }
        }

        return false;
      } catch (err) {
        if (window.personalised?.debugMode) console.error('Unexpected error in loadBundleData():', err);
        window.personalised.bundle.reset();
        return false;
      } finally {
        isLoading = false;
      }
    }

    // Public API
    return {
      waitForProductDataReady,
      initSubscriptionData,
      loadFromRechargeSubscription,
      loadBundleData
    };
  })();

  window.personalised.reviewData = reviewData;
})();
